import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  CircularProgress,
  Button,
  Tooltip,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckIcon from "@mui/icons-material/Check";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReplyIcon from "@mui/icons-material/Reply";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ForwardIcon from "@mui/icons-material/Forward";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import PermPhoneMsgOutlinedIcon from "@mui/icons-material/PermPhoneMsgOutlined";
import PartnerApiAuditPanel from "./PartnerApiAuditPanel";
import AiAgentAuditPanel from "./AiAgentAuditPanel";
import TextMessage from "./TextMessage";
import WhatsappInteractiveBlocks, { WhatsappInboundChoiceFooter } from "./WhatsappInteractiveBlocks";
import ImageMessage, {
  buildConversationImageGallery,
  getConversationImageGalleryIndex,
} from "./ImageMessage";
import AudioMessage from "./AudioMessage";
import VideoMessage from "./VideoMessage";
import DocumentMessage from "./DocumentMessage";
import LocationMessage from "./LocationMessage";
import ContactCardMessage from "./ContactCardMessage";
import VideoInviteMessage from "./VideoInviteMessage";
import VideoCallSummaryMessage, { resolveVideoCallSummary } from "./VideoCallSummaryMessage";
import VoiceCallLogMessage, { resolveVoiceCallLog } from "./VoiceCallLogMessage";
import { locationCardLines, locationCoordsForDisplay } from "./locationDisplay";
import { isVideoInviteMessage } from "../../utils/videoInviteMessage";
import { isXproVideoRecordingAttachment } from "./messageTypeRegistry";
import LogMessage from "./LogMessage";
import BPeEmittedMessage from "./BPeEmittedMessage";
import InternalNoteMessage from "./messages/InternalNoteMessage";
import PartsQuoteRequestMessage from "./messages/PartsQuoteRequestMessage";
import EmailMessageCard from "./messages/EmailMessageCard";
import OutboundEmailCard from "./messages/OutboundEmailCard";
import {
  getOutboundEmailDetails,
  isOutboundEmailMessage,
  splitStoredEmail,
} from "./messages/outboundEmail";
import { parseCustomerEmail } from "./emailCompose";
import { getMetaSocialFromMessage, metaSocialLabel } from "../../utils/metaMessageSocial";
import MetaCommentPostCard, {
  getMetaCommentPostFromMessage,
  stripCommentPostAppendix,
} from "./MetaCommentPostCard";
import {
  getWhatsappContextFromMessage,
  whatsappContextLabel,
} from "../../utils/whatsappMessageContext";
import { resolveWhatsappGroupSenderDisplayName } from "../../utils/whatsappQrGroupMentions";
import MessageSenderHeader from "./shared/MessageSenderHeader";
import InteractiveMessage from "./InteractiveMessage";
import {
  getDeliveryStatusDisplay,
  shouldShowDeliveryStatus,
  isReengagementError,
  REENGAGEMENT_FRIENDLY_MESSAGE,
  getFriendlyDeliveryError,
} from "./messageDeliveryStatus";
import { formatWhatsAppText } from "../../utils/whatsappTextFormat";
import {
  hasClientPhoneRegistered,
  shouldSkipClientPhoneForWhatsappGroupSend,
  outboundChannelRequiresClientPhone,
} from "../../utils/clientContactUtils";
import { parseRawEmail, prepareOriginalEmailHtml, resolveOriginalEmailHref } from "../../utils/parseRawEmail";
import {
  getMessageThreadHints,
  resolveQuotedPreview,
  getWhatsappForwardBannerLabel,
  isWhatsappAppOutboundFromMessage,
  getExternalOutboundSenderLabel,
  getExternalOutboundChipLabel,
} from "../../utils/messageThreadHints";
import {
  buildWhatsappInteractivePresentation,
  isTechnicalWhatsappChoiceId,
} from "../../utils/whatsappInteractiveDisplay";
import { parseSharedContactFromMessage } from "../../utils/parseSharedContact";
import { getMessageViaChannelLabel } from "../../utils/messageInteractionChannelLabel";
import { isPartsQuoteRequestMessage } from "../../utils/parsePartsQuoteRequest";
import { useNavigate } from "react-router-dom";
import { useCanViewApiAudit } from "../../hooks/useCanViewApiAudit";
import {
  getUraSlotFromMessage,
  buildChatbotSlotEditorPath,
} from "../../utils/uraSlotNavigation";
import { CHANNEL_TYPES } from "../../constants/channelCatalog";

/** Formata datas no padrão do xpro antigo (America/Araguaina) */
const parseDisplayDate = (value) => {
  if (!value) return "—";

  // Formato MySQL vindo do backend: "YYYY-MM-DD HH:mm:ss"
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "0000-00-00 00:00:00") return "—";
    const mysqlRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

    if (mysqlRegex.test(trimmed)) {
      // No xpro antigo, esse valor já está no fuso America/Araguaina (sem conversões).
      // Mantemos o horário e apenas reformatamos para DD/MM/YYYY HH:mm:ss (sem vírgula).
      const [datePart, timePart] = trimmed.split(" ");
      const [year, month, day] = datePart.split("-");
      return `${day}/${month}/${year} ${timePart}`;
    }
  }

  // Para datas JS/ISO, usa locale brasileiro (aproxima do antigo comportamento)
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "—";

  return d.toLocaleString("pt-BR");
};

/**
 * Anotação com PDF: `mensagem` no banco costuma repetir o nome do arquivo, que já aparece no link do DocumentMessage.
 */
function noteTextIsDuplicateOfLinkedDocument(message) {
  const doc = message?.document;
  if (!doc || typeof doc !== "object") return false;
  const docUrl = doc.url || doc.link;
  if (!docUrl || !String(docUrl).trim()) return false;
  const docName = String(doc.name || doc.fileName || doc.filename || "").trim();
  if (!docName) return false;
  const textRaw = message?.text != null ? String(message.text).trim() : "";
  if (!textRaw) return false;
  const stripHtml = (s) =>
    s
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .trim();
  const textPlain = stripHtml(textRaw);
  if (!textPlain) return false;
  const norm = (s) =>
    s
      .normalize("NFKC")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  return norm(textPlain) === norm(docName);
}

/**
 * Tipos de mensagem para alinhamento e estilo:
 * - Cliente: mensagem do cliente/BOT → esquerda, balão neutro
 * - Usuário logado: id_usuario/senderId da mensagem = id do usuário logado → direita, balão destacado
 * - Outros usuários: agent/operator mas não é o logado → direita, balão cor diferente
 * - WhatsApp (app): enviado pelo app no smartphone (canal), sem usuário Xpro → direita, balão “outros”
 */
const getSenderFlags = (message, user) => {
  const senderType = (message?.senderType || "").toLowerCase();
  const sender = (message?.sender || "").toLowerCase();

  // E-mail que a plataforma enviou (inclusive por regra de automação). Fica do
  // lado do atendente mesmo sem responsável na solicitação, senão aparece como
  // se o cliente tivesse escrito e o status de entrega some.
  const isOutboundEmail = isOutboundEmailMessage(message);

  const isSystem = (sender === "system" || senderType === "system") && !isOutboundEmail;

  const isWhatsappAppOutbound = isWhatsappAppOutboundFromMessage(message);

  const isClient =
    !isWhatsappAppOutbound &&
    !isOutboundEmail &&
    (sender === "cliente" ||
    sender === "client" ||
    senderType === "client" ||
    senderType === "customer" ||
    message?.channel === "customer");

  const isAgentOrOperator =
    isOutboundEmail ||
    sender === "agent" ||
    sender === "operator" ||
    senderType === "agent" ||
    senderType === "operator" ||
    senderType === "automation" ||
    senderType === "ai_agent" ||
    senderType === "chatbot";

  const loggedUserId = user?.id != null ? String(user.id) : null;
  const msgUserId =
    message?.senderId != null
      ? String(message.senderId)
      : message?.id_usuario != null
      ? String(message.id_usuario)
      : null;

  const isLoggedInUser =
    !!loggedUserId && !!msgUserId && msgUserId === loggedUserId;

  const isSystemOutbound =
    message?.systemOutbound === true &&
    message?.type !== "note" &&
    message?.type !== "log" &&
    message?.type !== "Xpro";

  const isOtherUser = !!user && !isClient && !isSystem && isAgentOrOperator && !isLoggedInUser;
  const isUser = isLoggedInUser || isOtherUser || isWhatsappAppOutbound || isSystemOutbound;

  return {
    isClient,
    isSystem,
    isUser,
    isLoggedInUser,
    isOtherUser,
    isWhatsappAppOutbound,
    isSystemOutbound,
  };
};

/** Rótulos em PT-BR para cabeçalhos do e-mail */
const EMAIL_HEADER_LABELS = {
  from: "De",
  to: "Para",
  cc: "Cc",
  bcc: "Cópia oculta",
  "reply-to": "Responder a",
  subject: "Assunto",
  date: "Data",
};

/** Iframe sandboxed: o clique no ANEXO é tratado aqui, porque o sandbox bloqueia navegação. */
function EmailOriginalFrame({ html }) {
  const frameRef = React.useRef(null);
  const srcDoc = React.useMemo(() => {
    const body = prepareOriginalEmailHtml(html);
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"></head><body style="margin:0;padding:8px;font-family:inherit;font-size:14px;">${body}</body></html>`;
  }, [html]);

  const onLoad = React.useCallback(() => {
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;
    doc.addEventListener(
      "click",
      (ev) => {
        const a = ev.target?.closest?.("a");
        if (!a) return;
        const rawHref = a.getAttribute("href") || "";
        const abs = resolveOriginalEmailHref(rawHref);
        if (!abs) {
          ev.preventDefault();
          return;
        }
        a.setAttribute("href", abs);
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
      },
      true
    );
  }, []);

  return (
    <iframe
      ref={frameRef}
      title="Corpo do e-mail"
      srcDoc={srcDoc}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      onLoad={onLoad}
      style={{ width: "100%", minHeight: 200, border: "none", display: "block" }}
    />
  );
}

/** Popup que exibe o e-mail original de forma legível (cabeçalhos + corpo), não em código bruto. */
function EmailOriginalDialog({ open, onClose, rawEmail }) {
  const parsed = React.useMemo(
    () => (open && rawEmail ? parseRawEmail(rawEmail) : null),
    [open, rawEmail]
  );

  const hasVisibleContent =
    parsed &&
    (Object.keys(parsed.friendlyHeaders || {}).length > 0 ||
      (parsed.bodyHtml && parsed.bodyHtml.trim()) ||
      (parsed.bodyText && parsed.bodyText.trim()));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: "85vh" } }}>
      <DialogTitle>E-mail original</DialogTitle>
      <DialogContent>
        {parsed && hasVisibleContent ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {Object.keys(parsed.friendlyHeaders).length > 0 && (
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  "& > div": { mb: 0.5 },
                  "& > div:last-of-type": { mb: 0 },
                }}
              >
                {Object.entries(parsed.friendlyHeaders).map(([key, value]) => (
                  <Box key={key} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Typography component="span" variant="body2" sx={{ fontWeight: 600, minWidth: "90px" }}>
                      {EMAIL_HEADER_LABELS[key] || key}:
                    </Typography>
                    <Typography component="span" variant="body2" sx={{ wordBreak: "break-word" }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            {(parsed.bodyHtml || parsed.bodyText) && (
              <Box
                sx={{
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  overflow: "auto",
                  maxHeight: "50vh",
                }}
              >
                {parsed.bodyHtml ? (
                  <EmailOriginalFrame html={parsed.bodyHtml} />
                ) : (
                  <Typography
                    component="pre"
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      m: 0,
                      fontFamily: "inherit",
                    }}
                  >
                    {parsed.bodyText}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <Box
            component="pre"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "monospace",
              fontSize: "0.8rem",
              p: 1.5,
              bgcolor: "action.hover",
              borderRadius: 1,
              overflow: "auto",
              maxHeight: "60vh",
            }}
          >
            {rawEmail || ""}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            try {
              navigator.clipboard.writeText(rawEmail || "");
            } catch (_) {}
          }}
        >
          Copiar
        </Button>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}

const MessageItem = ({
  message,
  user,
  onSendMessage,
  defaultChannel,
  conversationChannelId = null,
  conversationChannelLabel = null,
  personalThreadClientLabel = null,
  quotedLookup,
  onNavigateToQuotedMessage,
  onDeleteMessage,
  onReactMessage,
  onEditWhatsappMessage,
  onForwardWhatsappMessage,
  onQuoteWhatsappMessage,
  onEditNote,
  onDeleteNote,
  conversationMessages,
  messageIndex,
  conversationProjectId,
  noteMediaOnly = false,
  internalNoteCluster,
  selectedConversation = null,
}) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const navigate = useNavigate();
  const canViewApiAudit = useCanViewApiAudit();
  const ref = useRef();
  const [emailOriginalOpen, setEmailOriginalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [qrActionsAnchor, setQrActionsAnchor] = useState(null);
  const [, setDeleteWindowTick] = useState(0);

  const uraSlotNav = React.useMemo(() => {
    if (!canViewApiAudit) return null;
    if (message?.type === "log" || message?.type === "note" || message?.type === "Xpro") return null;
    return getUraSlotFromMessage(message);
  }, [canViewApiAudit, message]);
  const uraSlotEditorPath = uraSlotNav
    ? buildChatbotSlotEditorPath(uraSlotNav.chatbotUserId, uraSlotNav.slotId)
    : null;
  const canOpenUraSlot = Boolean(uraSlotEditorPath);

  const openUraSlotInEditor = React.useCallback(() => {
    if (!uraSlotEditorPath) return;
    navigate(uraSlotEditorPath);
  }, [navigate, uraSlotEditorPath]);

  const { isClient, isSystem, isUser, isLoggedInUser, isOtherUser, isWhatsappAppOutbound, isSystemOutbound } =
    getSenderFlags(message, user);
  const agentSideVisual = isOtherUser || isWhatsappAppOutbound || isSystemOutbound;
  const hasEmailOriginal = !!(
    (message?.emailOriginal && String(message.emailOriginal).trim()) ||
    message?.hasEmailOriginal
  );

  const conversationImageGallery = React.useMemo(
    () => buildConversationImageGallery(conversationMessages),
    [conversationMessages]
  );
  const conversationImageIndex = React.useMemo(
    () => getConversationImageGalleryIndex(conversationMessages, message),
    [conversationMessages, message]
  );

  // Identificação visual por tipo de automação:
  // - Agente de IA: respostas automáticas (tipo 35 / AutoReply).
  // - Chatbot: mensagem atribuída a usuário de tipo chatbot, quando não for AutoReply.
  const isAiAgentMessage =
    (message?.senderType || "").toLowerCase() === "ai_agent" ||
    message?.type === "automaticanswer" ||
    message?.channel === "AutoReply";
  const isChatbotMessage =
    !isAiAgentMessage &&
    ((message?.senderType || "").toLowerCase() === "chatbot" ||
      (((String(message?.senderType || "").toLowerCase() === "agent") ||
        (String(message?.sender || "").toLowerCase() === "agent")) &&
        Number(message?.uraUser) === 1));

  const whatsappMeta = React.useMemo(() => {
    const raw = message?.json_whatsapp;
    if (!raw) return null;
    if (typeof raw === "object") return raw;
    if (typeof raw !== "string") return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, [message?.json_whatsapp]);

  const isVoicemail = Boolean(whatsappMeta?.xpro_voice?.voicemail);
  const spokenOnCall = Boolean(
    isVoicemail ||
      whatsappMeta?.xpro_voice?.spoken ||
      whatsappMeta?.xpro_transport === "twilio_voice"
  );
  const spokenLabel = isVoicemail ? "Caixa postal" : "Falado na ligação";

  const threadHints = React.useMemo(
    () => getMessageThreadHints(message),
    [message?.json_whatsapp, message?.emailOriginalTags]
  );

  const quotedPreview = React.useMemo(() => {
    if (!quotedLookup) return null;
    return resolveQuotedPreview(message, quotedLookup, user);
  }, [quotedLookup, message, user, message?.id, message?.json_whatsapp, message?.emailOriginalTags]);

  const waChannelBlob = `${message?.channel || ""} ${defaultChannel || ""}`;
  const isLikelyWhatsapp = /\bwhatsapp\b/i.test(waChannelBlob);

  const waInteractivePres = React.useMemo(() => {
    if (!isLikelyWhatsapp || !Array.isArray(conversationMessages) || messageIndex == null) return null;
    return buildWhatsappInteractivePresentation(message, messageIndex, conversationMessages);
  }, [isLikelyWhatsapp, message, message?.id, message?.json_whatsapp, message?.text, messageIndex, conversationMessages]);

  const sharedContact = React.useMemo(
    () => parseSharedContactFromMessage(message),
    [message?.contact, message?.text, message?.json_whatsapp, message?.type]
  );

  const waClientTextOverride = React.useMemo(() => {
    if (!isLikelyWhatsapp || !isClient || !waInteractivePres) return undefined;
    const t = message?.text != null ? String(message.text).trim() : "";
    if (!t) return undefined;
    const { inboundHumanFromPayload, inboundChoice, numberedFallbackLabel } = waInteractivePres;
    if (isTechnicalWhatsappChoiceId(t)) {
      const better =
        (inboundHumanFromPayload && inboundHumanFromPayload !== t ? inboundHumanFromPayload : "") ||
        (inboundChoice?.title && inboundChoice.title !== t ? inboundChoice.title : "") ||
        numberedFallbackLabel ||
        "";
      return better || undefined;
    }
    if (inboundChoice?.id && inboundChoice.title && t === inboundChoice.id && inboundChoice.title !== t) {
      return inboundChoice.title;
    }
    return undefined;
  }, [isLikelyWhatsapp, isClient, waInteractivePres, message?.text]);

  const metaCommentPost = React.useMemo(() => {
    const post = getMetaCommentPostFromMessage(message);
    if (!post) return null;
    if (!post.imageUrl && message?.image && String(message.image).trim()) {
      return { ...post, imageUrl: String(message.image).trim() };
    }
    return post;
  }, [message?.json_whatsapp, message?.image]);

  const commentPostTextOverride = React.useMemo(() => {
    if (!metaCommentPost) return undefined;
    const raw = message?.text != null ? String(message.text) : "";
    const stripped = stripCommentPostAppendix(raw);
    if (stripped && stripped !== raw) return stripped;
    return undefined;
  }, [metaCommentPost, message?.text]);

  const displayTextOverride = waClientTextOverride ?? commentPostTextOverride;

  const isOutboundFromWhatsappApp =
    !isClient &&
    (whatsappMeta?.xpro_origin === "whatsapp_app" || whatsappMeta?.xpro_origin === "meta_page") &&
    whatsappMeta?.xpro_direction === "outbound_external";

  /** Reações recebidas via webhook (Meta / BSP / API Fácil), persistidas em json_whatsapp.xpro_reactions */
  const waForwardLabel = React.useMemo(
    () => (isLikelyWhatsapp ? getWhatsappForwardBannerLabel(message) : null),
    [isLikelyWhatsapp, message?.json_whatsapp, message?.isForwardedWhatsapp]
  );

  const waReactionChips = React.useMemo(() => {
    if (!isLikelyWhatsapp || !whatsappMeta || typeof whatsappMeta !== "object") return null;
    const xr = whatsappMeta.xpro_reactions;
    if (!xr || typeof xr !== "object" || !xr.byReactor || typeof xr.byReactor !== "object") return null;
    const entries = Object.values(xr.byReactor).filter((v) => v && v.emoji && String(v.emoji).trim());
    if (!entries.length) return null;
    const counts = new Map();
    for (const v of entries) {
      const em = String(v.emoji).trim();
      counts.set(em, (counts.get(em) || 0) + 1);
    }
    return Array.from(counts.entries()).map(([emoji, n], idx) => ({
      key: `${emoji}-${idx}`,
      label: n > 1 ? `${emoji} · ${n}` : emoji,
      tooltip: n > 1 ? `${n} participantes reagiram com ${emoji}` : "Reação no WhatsApp",
    }));
  }, [isLikelyWhatsapp, whatsappMeta]);

  const isImportedFromPersonalWhatsapp = Boolean(
    whatsappMeta?.imported_from_personal_whatsapp === true ||
      whatsappMeta?.imported_from_personal_whatsapp === "true" ||
      whatsappMeta?.xpro_origem_whatsapp_perfil === true ||
      whatsappMeta?.xpro_origem_whatsapp_perfil === "true"
  );

  const isGroupParticipant = Boolean(
    message?.isGroupMessage ||
      message?.group?.id ||
      whatsappMeta?.xpro_group?.senderClientId
  );
  const groupName =
    message?.group?.name ||
    whatsappMeta?.xpro_group?.name ||
    null;
  const participantDisplayName = isGroupParticipant && isClient
    ? resolveWhatsappGroupSenderDisplayName(message, { groupName })
    : message?.participant?.name || null;

  const getAlignment = () => {
    if (message.type === "log") return "center";
    if (message.type === "note" || message.type === "Xpro") {
      return isLoggedInUser ? "flex-end" : "flex-start";
    }
    if (isUser) return "flex-end";
    if (isClient) return "flex-start";
    return "flex-start";
  };

  /** Fundo e texto da bolha (mensagens normais). Usado também para cor de legendas dentro da bolha (status, enviando). */
  const getChatBubblePalette = () => {
    const isDark = mode === "dark";
    const clientBg = theme.palette.messages?.client ?? (isDark ? "#3a3f44" : "#e8ecf0");
    const clientColor = theme.palette.messages?.clientContrast ?? (isDark ? "#e4e6eb" : "#1c1e21");
    const loggedInUserBg = theme.palette.messages?.user ?? (isDark ? "#0b93f6" : "#0b93f6");
    const loggedInUserColor = theme.palette.messages?.userContrast ?? "#ffffff";
    const otherUserBg = theme.palette.messages?.others ?? (isDark ? "#4a5568" : "#d4dce4");
    const otherUserColor = theme.palette.messages?.othersContrast ?? (isDark ? "#e4e6eb" : "#1c1e21");
    const bg = isLoggedInUser ? loggedInUserBg : agentSideVisual ? otherUserBg : clientBg;
    const fg = isLoggedInUser ? loggedInUserColor : agentSideVisual ? otherUserColor : clientColor;
    return { bg, fg };
  };

  const chatBubblePalette =
    message.type === "log" || message.type === "note" || message.type === "Xpro"
      ? null
      : getChatBubblePalette();

  /** Legendas dentro da bolha: não usar text.secondary (é do tema global e pode falhar em bolhas escuras no modo claro). */
  const captionOnBubbleMutedColor = chatBubblePalette
    ? alpha(chatBubblePalette.fg, 0.88)
    : theme.palette.text.secondary;

  const getMessageStyle = () => {
    if (message.type === "log") {
      const logBg = mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100];
      return {
        backgroundColor: logBg,
        borderRadius: "14px",
        padding: "8px 14px",
        maxWidth: "100%",
        color: theme.palette.text.primary,
        textAlign: "center",
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        boxShadow:
          mode === "dark"
            ? "0px 1px 3px rgba(0, 0, 0, 0.3)"
            : "0px 1px 3px rgba(0, 0, 0, 0.08)",
      };
    }

    if (message.type === "note" || message.type === "Xpro") {
      return {
        backgroundColor: theme.palette.messages?.notes || theme.palette.action.hover,
        color: theme.palette.text.primary,
        borderLeft: `4px solid ${theme.palette.warning.main}`,
        borderRadius: "8px",
        padding: "12px",
        maxWidth: "100%",
        boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
        textAlign: "center"
      };
    }

    // Três estilos: cliente (esquerda, neutro) | usuário logado (direita, destacado) | outros usuários (direita, cor diferente)
    const { bg, fg } = getChatBubblePalette();

    return {
      backgroundColor: bg,
      color: fg,
        borderRadius: "12px",
        padding: "12px",
        maxWidth: "100%",
        "@media (max-width: 768px)": {
          padding: "10px 12px",
        },
      boxShadow: isLoggedInUser
        ? "0px 1px 2px rgba(0, 0, 0, 0.2)"
        : "0px 1px 2px rgba(0, 0, 0, 0.08)",
      border: isLoggedInUser
        ? "none"
        : agentSideVisual
          ? `1px solid ${alpha(theme.palette.divider, 0.9)}`
          : undefined,
      ...(canOpenUraSlot
        ? {
            cursor: "pointer",
            transition: "box-shadow 0.15s ease, transform 0.15s ease",
            "&:hover": {
              boxShadow: isLoggedInUser
                ? "0px 2px 6px rgba(0, 0, 0, 0.28)"
                : "0px 2px 6px rgba(0, 0, 0, 0.14)",
              transform: "translateY(-1px)",
            },
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          }
        : {}),
    };
  };

  /** Renderiza mídia extra (imagem, vídeo, áudio, documento) quando presente em tipos mistos.
   *  Localização não entra aqui: só mensagens com `type === "location"` exibem bloco de mapa/coords,
   *  para evitar lixo de lat/lng/endereço vazio vindos do banco em mensagens de imagem/texto/etc.
   *  Para evitar duplicidade, o tipo principal (image, video, audio, document) é tratado no switch.
   */
  const renderMedia = (opts = {}) => {
    const skipImage = Boolean(opts.skipImage);
    const parts = [];
    if (!skipImage && message.image && String(message.image).trim() && message.type !== "image") {
      parts.push(
        <Box key="img" sx={{ mb: message.text ? 1 : 0 }}>
          <ImageMessage
            message={message}
            galleryUrls={conversationImageGallery}
            galleryIndex={conversationImageIndex}
          />
        </Box>
      );
    }
    if (message.video && String(message.video).trim() && message.type !== "video") {
      parts.push(
        <Box key="video" sx={{ mb: message.text ? 1 : 0 }}>
          <VideoMessage message={{ ...message, url: message.video || message.url }} />
        </Box>
      );
    }
    if (
      message.audio &&
      String(message.audio).trim() &&
      message.type !== "audio" &&
      !resolveVoiceCallLog(message)
    ) {
      parts.push(
        <Box key="audio" sx={{ mb: message.text ? 1 : 0 }}>
          <AudioMessage message={message} />
        </Box>
      );
    }
    if (message.document && message.type !== "document") {
      const doc = message.document;
      const docUrl = typeof doc === "string" ? doc : (doc?.url || doc?.link);
      const docName = typeof doc === "object" && doc != null ? (doc.name || doc.fileName || doc.filename) : null;
      if (docUrl && String(docUrl).trim()) {
        parts.push(
          <Box key="doc" sx={{ mb: message.text ? 1 : 0 }}>
            <DocumentMessage message={{ ...message, url: docUrl, name: docName || "Documento" }} />
          </Box>
        );
      }
    }
    if (parts.length === 0) return null;
    return <>{parts}</>;
  };

  const channelLabel = getMessageViaChannelLabel(message, conversationChannelLabel, {
    channelId: conversationChannelId,
    channelLabel: conversationChannelLabel,
    isChatbotSimulation:
      Boolean(message?.isChatbotSimulation) ||
      Boolean(selectedConversation?.isChatbotSimulation),
  });
  const isEmailChannel =
    /email/i.test(channelLabel) ||
    /email/i.test(String(message?.channel || "")) ||
    /email/i.test(String(message?.channelName || ""));

  const outboundEmailDetails = getOutboundEmailDetails(message);
  const isOutboundEmail = Boolean(outboundEmailDetails);
  const isAutomationEmail = Boolean(outboundEmailDetails?.automation);

  const renderTextBody = () => {
    if (sharedContact) {
      return (
        <ContactCardMessage
          contact={sharedContact}
          presentation={isLikelyWhatsapp ? "whatsapp" : "default"}
          conversationProjectId={conversationProjectId}
        />
      );
    }
    if (isVideoInviteMessage(message)) {
      return (
        <VideoInviteMessage
          message={message}
          bubbleFg={chatBubblePalette?.fg}
          onAccentBubble={Boolean(isLoggedInUser)}
        />
      );
    }
    const textNode = <TextMessage message={message} textOverride={displayTextOverride} />;
    if (isOutboundEmail) {
      // O assunto já aparece no cabeçalho do card.
      const corpo = splitStoredEmail(displayTextOverride ?? message?.text).body;
      return (
        <OutboundEmailCard message={message}>
          <TextMessage message={message} textOverride={corpo} />
        </OutboundEmailCard>
      );
    }
    if (isEmailChannel && isClient) {
      const inbound = parseCustomerEmail(message);
      return (
        <EmailMessageCard
          message={message}
          attachments={message.emailAttachments}
          hasOriginal={hasEmailOriginal}
          onOpenOriginal={() => setEmailOriginalOpen(true)}
          conversationImageGallery={conversationImageGallery}
        >
          <TextMessage message={message} textOverride={inbound.body || ""} />
        </EmailMessageCard>
      );
    }
    return textNode;
  };

  const renderContent = () => {
    const skipPostImageInMedia = Boolean(metaCommentPost?.imageUrl || metaCommentPost?.permalink);
    const mediaBlock = renderMedia({ skipImage: skipPostImageInMedia });
    const metaSocial = getMetaSocialFromMessage(message);
    const waCtx = getWhatsappContextFromMessage(message);
    const waChipLabel = whatsappContextLabel(waCtx);
    const metaSocialChipLabel = metaSocialLabel(metaSocial) || waChipLabel;
    const metaReaction = metaSocial?.lastReaction;
    const displayText = displayTextOverride != null
      ? String(displayTextOverride)
      : String(message.text || message.mensagem || "");
    const hasMediaOnly =
      !displayText.trim() &&
      Boolean(
        (!skipPostImageInMedia && message.image) ||
          message.video ||
          message.audio ||
          message.document
      );
    const metaSocialBlock =
      metaSocialChipLabel || metaReaction?.emoji ? (
        <Box sx={{ mb: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {metaSocialChipLabel ? (
            <Chip
              size="small"
              label={metaSocialChipLabel}
              variant="outlined"
              sx={{ height: 22, fontSize: "0.7rem" }}
            />
          ) : null}
          {metaReaction?.emoji ? (
            <Chip size="small" label={`Reação: ${metaReaction.emoji}`} sx={{ height: 22, fontSize: "0.7rem" }} />
          ) : null}
        </Box>
      ) : null;

    switch (message.type) {
      case "text":
        return (
          <>
            {metaSocialBlock}
            {mediaBlock}
            {hasMediaOnly ? null : renderTextBody()}
            {isLikelyWhatsapp && isClient && !sharedContact && (
              <WhatsappInboundChoiceFooter
                message={message}
                textOverride={displayTextOverride}
                defaultChannel={defaultChannel}
              />
            )}
            {!isVideoInviteMessage(message) && (
              <WhatsappInteractiveBlocks message={message} isClient={isClient} />
            )}
          </>
        );
      case "contact":
        return (
          <>
            {mediaBlock}
            {renderTextBody()}
            {isLikelyWhatsapp && isClient && !sharedContact && (
              <WhatsappInboundChoiceFooter
                message={message}
                textOverride={waClientTextOverride}
                defaultChannel={defaultChannel}
              />
            )}
            <WhatsappInteractiveBlocks message={message} isClient={isClient} />
          </>
        );
      case "image": {
        const rawImageText = message.text && String(message.text).trim();
        const imageText = commentPostTextOverride
          ? String(commentPostTextOverride).trim()
          : rawImageText;
        const isOnlyHtmlLink =
          imageText && /^\s*<a\s+href=["'][^"']+["'][^>]*>[\s\S]*?<\/a>\s*$/i.test(imageText);
        const isPlaceholderOnly = imageText === "[mensagem]";
        const isBracketFallback =
          imageText && /^\[(Cliente mencionou|Cliente respondeu|Reação ao|Link compartilhado|Localização|Agendamento|Opt-in|Mensagem não suportada)/i.test(imageText);
        const showImageCaption =
          imageText && !isOnlyHtmlLink && !isPlaceholderOnly && !isBracketFallback;
        const hideNativeImage = Boolean(metaCommentPost);
        return (
          <>
            {metaSocialBlock}
            {/* Mensagem principal do tipo imagem (omitida se o card da publicação já mostra) */}
            {!hideNativeImage && message.image && String(message.image).trim() && (
              <Box sx={{ mb: showImageCaption ? 1 : 0 }}>
                <ImageMessage
                  message={message}
                  galleryUrls={conversationImageGallery}
                  galleryIndex={conversationImageIndex}
                />
              </Box>
            )}
            {/* Anexos adicionais de mídia, se existirem */}
            {mediaBlock}
            {showImageCaption && (
              <Typography
                sx={{ mt: 0.5, "& a": { color: "primary.main", wordBreak: "break-all" }, "& code": { fontFamily: "monospace", fontSize: "0.9em" } }}
                component="div"
                style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                dangerouslySetInnerHTML={{ __html: formatWhatsAppText(imageText) }}
              />
            )}
          </>
        );
      }
      case "audio":
        return (
          <>
            {mediaBlock}
            <AudioMessage message={message} />
          </>
        );
      case "video":
        return (
          <>
            {mediaBlock}
            <VideoMessage message={{ ...message, url: message.video || message.url }} />
          </>
        );
      case "document":
        return (
          <>
            {mediaBlock}
            {message.document && (() => {
              const doc = message.document;
              const docUrl = typeof doc === "string" ? doc : (doc?.url || doc?.link);
              const docName = typeof doc === "object" && doc != null ? (doc.name || doc.fileName || doc.filename) : "Documento";
              return docUrl ? <DocumentMessage message={{ ...message, url: docUrl, name: docName }} /> : null;
            })()}
          </>
        );
      case "sticker":
        return (
          <>
            {mediaBlock}
            {(message.image || message.sticker || message.url) && (
              <Box sx={{ maxWidth: 180 }}>
                <ImageMessage
                  message={{
                    ...message,
                    image: message.image || message.sticker || message.url,
                  }}
                />
              </Box>
            )}
          </>
        );
      case "poll": {
        let pollJson = message.json_whatsapp;
        if (typeof pollJson === "string") {
          try {
            pollJson = JSON.parse(pollJson);
          } catch {
            pollJson = null;
          }
        }
        const options = pollJson?.options || pollJson?.pollOptions || pollJson?.xpro_whatsapp_context?.options || [];
        return (
          <>
            {mediaBlock}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {message.text || "Enquete"}
              </Typography>
              {Array.isArray(options)
                ? options.map((opt, idx) => (
                    <Typography key={idx} variant="body2" color="text.secondary">
                      • {typeof opt === "string" ? opt : opt?.name || opt?.option || ""}
                    </Typography>
                  ))
                : null}
            </Box>
          </>
        );
      }
      case "location":
        return (
          <>
            {mediaBlock}
            {locationCardLines(message.location).primary ||
            locationCoordsForDisplay(message.location) ? (
              <LocationMessage
                message={message}
                presentation={isLikelyWhatsapp ? "whatsapp" : "default"}
              />
            ) : null}
          </>
        );
      case "bpe":
        return (
          <>
            {mediaBlock}
            <BPeEmittedMessage message={message} />
          </>
        );
      case "log":
        return (
          <>
            {mediaBlock}
            {resolveVoiceCallLog(message) ? (
              <VoiceCallLogMessage message={message} />
            ) : resolveVideoCallSummary(message) ? (
              <VideoCallSummaryMessage message={message} />
            ) : (
              <LogMessage message={message} />
            )}
          </>
        );
      case "interactive":
        return (
          <>
            {mediaBlock}
            <InteractiveMessage message={message} />
          </>
        );
      case "note": {
        const noteTextTrimmed = message.text != null ? String(message.text).trim() : "";
        const showNoteBody =
          noteTextTrimmed !== "" && !noteTextIsDuplicateOfLinkedDocument(message);
        return (
          <>
            {mediaBlock}
            {showNoteBody && (
              <Typography
                component="div"
                sx={{
                  color: "inherit",
                  "& a": { color: "primary.main", wordBreak: "break-all" },
                  "& code": { fontFamily: "monospace", fontSize: "0.9em" },
                }}
                style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                dangerouslySetInnerHTML={{ __html: formatWhatsAppText(message.text) }}
              />
            )}
          </>
        );
      }
      default:
        return (
          <>
            {metaSocialBlock}
            {mediaBlock}
            {sharedContact ? (
              <ContactCardMessage
                contact={sharedContact}
                presentation={isLikelyWhatsapp ? "whatsapp" : "default"}
                conversationProjectId={conversationProjectId}
              />
            ) : hasMediaOnly ? null : (
              <Typography
                component="div"
                sx={{
                  "& a": { color: "primary.main", wordBreak: "break-all" },
                  "& code": { fontFamily: "monospace", fontSize: "0.9em" },
                }}
                style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                dangerouslySetInnerHTML={{
                  __html: formatWhatsAppText(message.text || "Mensagem desconhecida"),
                }}
              />
            )}
            {!isVideoInviteMessage(message) && (
              <WhatsappInteractiveBlocks message={message} isClient={isClient} />
            )}
          </>
        );
    }
  };

  // Para logs: exibir nome do usuário que registrou apenas se id_usuario estiver preenchido; caso contrário não mostrar nada
  const hasLogUserId =
    message.type === "log" &&
    ((message?.id_usuario != null && message?.id_usuario !== "" && Number(message?.id_usuario) !== 0) ||
      (message?.senderId != null && message?.senderId !== "" && Number(message?.senderId) !== 0));
  const displayName =
    message.type === "log"
      ? hasLogUserId
        ? (message?.senderName || message?.userName || "")
        : ""
      : isAiAgentMessage
      ? (message?.senderName && message.senderName !== "Agente de IA"
          ? message.senderName
          : "Agente de IA")
      : isChatbotMessage
      ? (message?.senderName && message.senderName !== "Chatbot"
          ? message.senderName
          : "Chatbot")
      : isOutboundEmail
      ? outboundEmailDetails?.automation
        ? "Automação"
        : message?.senderName || "Atendimento"
      : isGroupParticipant && isClient
      ? participantDisplayName
      : message?.senderName ||
        (isSystem
          ? "Sistema"
          : isClient
          ? participantDisplayName || personalThreadClientLabel?.trim() || "Cliente"
          : isLoggedInUser
          ? "Você"
          : isWhatsappAppOutbound
          ? getExternalOutboundSenderLabel(message)
          : agentSideVisual
          ? "Atendente"
          : "Usuário");

  // Data/hora: para e-mail recebido (inbound) prioriza createdAt (recebimento); senão updatedAt/timestamp
  const displayDate = parseDisplayDate(
    message?.createdAt ||
    message?.updatedAt ||
    message?.lastUpdate ||
    message?.timestamp
  );
  const isInboundEmail = isEmailChannel && isClient;

  const deliveryDisplay = getDeliveryStatusDisplay(message);
  // O card de e-mail enviado já exibe entrega e motivo de falha no cabeçalho.
  const showDeliveryStatus = shouldShowDeliveryStatus(message, isUser) && !isOutboundEmail;
  const isReengagement = isReengagementError(deliveryDisplay?.errorReason);
  const isSending =
    message.status === "sending" || (showDeliveryStatus && deliveryDisplay.isPending);
  const isErrorStatus =
    message.status === "error" || (showDeliveryStatus && deliveryDisplay.isError);
  const looksLikeChannelId = (v) => {
    if (v == null || v === "") return false;
    const s = String(v).trim();
    if (!s) return false;
    // IDs numéricos ou tokens especiais de canal (nota interna / WhatsApp pessoal)
    if (/^\d+$/.test(s)) return true;
    if (s === "internal_note" || s === "wa_personal" || s.startsWith("wa-personal")) return true;
    return false;
  };
  const retryChannel = (() => {
    if (message.channelId != null && String(message.channelId).trim() !== "") {
      return message.channelId;
    }
    if (looksLikeChannelId(message.channel)) return message.channel;
    return conversationChannelId ?? null;
  })();
  const retryChannelType =
    message.channelType ||
    (!looksLikeChannelId(message.channel) ? message.channel : null) ||
    defaultChannel;
  const retryBlockedByPhone =
    selectedConversation &&
    outboundChannelRequiresClientPhone(retryChannel, retryChannelType) &&
    !hasClientPhoneRegistered(selectedConversation) &&
    !shouldSkipClientPhoneForWhatsappGroupSend({
      conversation: selectedConversation,
      messages: conversationMessages,
      channelTipo: message?.originTipo,
      channelType: retryChannelType,
    });
  const canRetry =
    isLoggedInUser &&
    typeof onSendMessage === "function" &&
    (message.status === "error" || (deliveryDisplay.isError && message.text)) &&
    !isReengagement &&
    !retryBlockedByPhone;

  const deleteDeadlineSec =
    message?.platformDeleteDeadlineUnix != null
      ? Number(message.platformDeleteDeadlineUnix)
      : null;
  const withinProviderDeleteWindow =
    deleteDeadlineSec == null ||
    !Number.isFinite(deleteDeadlineSec) ||
    Date.now() / 1000 < deleteDeadlineSec;

  React.useEffect(() => {
    if (!message?.canDeleteFromPlatform || deleteDeadlineSec == null || !Number.isFinite(deleteDeadlineSec)) {
      return undefined;
    }
    const ms = deleteDeadlineSec * 1000 - Date.now();
    if (ms <= 0) return undefined;
    const id = setTimeout(() => setDeleteWindowTick((n) => n + 1), ms + 400);
    return () => clearTimeout(id);
  }, [message?.id, message?.canDeleteFromPlatform, deleteDeadlineSec]);

  const isWhatsappQrOrigin = Number(message?.originTipo) === CHANNEL_TYPES.WHATSAPP_QR;
  const showDeleteAction =
    !isWhatsappQrOrigin &&
    Boolean(message?.canDeleteFromPlatform) &&
    !message?.deletedAt &&
    withinProviderDeleteWindow &&
    isUser &&
    typeof onDeleteMessage === "function" &&
    !isSending &&
    message?.status !== "sending";
  const canQuoteMessage =
    !isWhatsappQrOrigin &&
    Boolean(message?.whatsappQrActions?.quote) &&
    typeof onQuoteWhatsappMessage === "function";
  const canReactMessage =
    Boolean(message?.whatsappQrActions?.react) && typeof onReactMessage === "function";
  const canEditMessage =
    Boolean(message?.whatsappQrActions?.edit) && typeof onEditWhatsappMessage === "function";
  const canForwardMessage =
    Boolean(message?.whatsappQrActions?.forward) && typeof onForwardWhatsappMessage === "function";
  const hasMoreQrActions = canReactMessage || canEditMessage || canForwardMessage;
  const hasBubbleActions = canQuoteMessage || showDeleteAction || hasMoreQrActions;
  const bubbleActionColor =
    isLoggedInUser
      ? (theme.palette.messages?.userContrast ?? "#ffffff")
      : agentSideVisual
        ? (theme.palette.messages?.othersContrast ?? theme.palette.text.primary)
        : isClient
          ? (theme.palette.messages?.clientContrast ?? theme.palette.text.primary)
          : theme.palette.text.secondary;

  const renderStatusIcon = () => {
    if (deliveryDisplay.icon === "pending") return <ScheduleIcon sx={{ fontSize: 14 }} />;
    if (deliveryDisplay.icon === "sent") return <CheckIcon sx={{ fontSize: 14 }} />;
    if (deliveryDisplay.icon === "delivered" || deliveryDisplay.icon === "read")
      return <DoneAllIcon sx={{ fontSize: 14 }} />;
    if (deliveryDisplay.icon === "error") return <ErrorOutlineIcon sx={{ fontSize: 14 }} />;
    return null;
  };

  const quoteInteractive =
    typeof onNavigateToQuotedMessage === "function" && quotedPreview?.matchedMessageId != null;
  let quotedPreviewBlock = null;
  if (
    quotedPreview &&
    message.type !== "log" &&
    message.type !== "note" &&
    message.type !== "Xpro"
  ) {
    const quoteBox = (
      <Box
        component={quoteInteractive ? "button" : "div"}
        type={quoteInteractive ? "button" : undefined}
        onClick={
          quoteInteractive
            ? () => onNavigateToQuotedMessage(quotedPreview.matchedMessageId)
            : undefined
        }
        sx={{
          borderLeft: "3px solid",
          borderColor: isLoggedInUser
            ? "rgba(255,255,255,0.55)"
            : agentSideVisual
            ? "rgba(0,0,0,0.22)"
            : "primary.main",
          pl: 1,
          py: 0.5,
          mb: threadHints.chips.length ? "4px" : "8px",
          mt: "-2px",
          maxWidth: "100%",
          borderRadius: "4px",
          bgcolor:
            isLoggedInUser
              ? "rgba(0,0,0,0.08)"
              : agentSideVisual
              ? "rgba(0,0,0,0.04)"
              : mode === "dark"
              ? "rgba(255,255,255,0.06)"
              : "rgba(0,0,0,0.04)",
          ...(quoteInteractive
            ? {
                border: "none",
                cursor: "pointer",
                textAlign: "left",
                font: "inherit",
                color: "inherit",
                width: "100%",
                display: "block",
                appearance: "none",
                WebkitAppearance: "none",
                transition: "opacity 0.15s ease",
                "&:hover": { opacity: 0.92 },
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: 2,
                },
              }
            : {}),
        }}
      >
        <Typography
          variant="caption"
          component="div"
          sx={{
            fontWeight: 700,
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "inherit",
          }}
        >
          {quotedPreview.author}
        </Typography>
        <Typography
          variant="caption"
          component="div"
          sx={{
            mt: "2px",
            lineHeight: 1.35,
            color: "inherit",
            opacity: 0.88,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            wordBreak: "break-word",
          }}
        >
          {quotedPreview.text}
        </Typography>
      </Box>
    );
    quotedPreviewBlock = quoteInteractive ? (
      <Tooltip title="Ir para a mensagem original">{quoteBox}</Tooltip>
    ) : (
      quoteBox
    );
  }

  if (message.type === "log") {
    return null;
  }

  // Gravação Twilio: card centrado (não bolha WhatsApp).
  if (isXproVideoRecordingAttachment(message)) {
    return (
      <Box
        ref={ref}
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          py: 0.75,
          px: 1,
        }}
      >
        <VideoCallSummaryMessage message={message} />
      </Box>
    );
  }

  if (isPartsQuoteRequestMessage(message)) {
    const quoteDisplayName =
      message?.senderName ||
      message?.userName ||
      message?.clientName ||
      (isClient ? null : isLoggedInUser ? "Você" : "Equipe");
    return (
      <Box
        ref={ref}
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          pt: 1,
          pb: 1,
          pr: "15%",
        }}
      >
        <PartsQuoteRequestMessage message={message} displayName={quoteDisplayName} />
      </Box>
    );
  }

  if ((message.type === "note" || message.type === "Xpro") && !noteMediaOnly) {
    const noteDisplayName =
      message?.senderName ||
      message?.userName ||
      (isLoggedInUser ? "Você" : "Equipe");
    const cluster = internalNoteCluster || {
      isGrouped: false,
      isFirst: true,
      isLast: true,
      isOnly: true,
    };
    const isNoteContinuation = cluster.isGrouped && !cluster.isFirst;
    return (
      <Box
        ref={ref}
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: isLoggedInUser ? "flex-end" : "flex-start",
          pt: isNoteContinuation ? 0 : 1,
          pb: cluster.isGrouped && !cluster.isLast ? 0 : 1,
          pl: isLoggedInUser ? "15%" : 0,
          pr: isLoggedInUser ? 0 : "15%",
        }}
      >
        <InternalNoteMessage
          message={message}
          displayName={noteDisplayName}
          isOwnNote={isLoggedInUser}
          cluster={cluster}
          onEditNote={onEditNote}
          onDeleteNote={onDeleteNote}
        >
          {renderMedia()}
        </InternalNoteMessage>
      </Box>
    );
  }

  if (noteMediaOnly) {
    return <Box ref={ref}>{renderMedia()}</Box>;
  }

  if (message.deletedAt) {
    return (
      <Box
        ref={ref}
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: isLoggedInUser || isUser ? "flex-end" : "flex-start",
          py: 0.5,
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.25,
            py: 0.5,
            borderRadius: "10px",
            border: `1px dashed ${alpha(theme.palette.text.primary, 0.22)}`,
            color: theme.palette.text.disabled,
          }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
          <Typography variant="caption" sx={{ fontStyle: "italic", fontSize: "0.72rem" }}>
            Excluído
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      {metaCommentPost ? (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            px: 1,
            pt: 1,
            pb: 0.5,
          }}
        >
          <MetaCommentPostCard post={metaCommentPost} />
        </Box>
      ) : null}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: getAlignment(),
          paddingTop: metaCommentPost ? "4px" : "8px",
          paddingBottom: "8px",
          width: "100%",
          // Logs e notas devem ficar totalmente centralizados, sem recuos laterais extras.
          pl:
            message.type === "log" || message.type === "note" || message.type === "Xpro"
              ? 0
              : isUser
              ? "15%"
              : 0,
          pr:
            message.type === "log" || message.type === "note" || message.type === "Xpro"
              ? 0
              : isUser
              ? 0
              : "15%",
          "@media (max-width: 768px)": {
            paddingTop: metaCommentPost ? "4px" : "6px",
            paddingBottom: "6px",
            pl:
              message.type === "log" || message.type === "note" || message.type === "Xpro"
                ? 0
                : isUser
                ? "6%"
                : 0,
            pr:
              message.type === "log" || message.type === "note" || message.type === "Xpro"
                ? 0
                : isUser
                ? 0
                : "6%",
          },
        }}
      >
      <Box
        sx={{ ...getMessageStyle() }}
        onClick={
          canOpenUraSlot
            ? (event) => {
                const interactive = event.target.closest(
                  "button, a, input, textarea, [data-no-ura-nav]"
                );
                if (interactive && interactive !== event.currentTarget) return;
                openUraSlotInEditor();
              }
            : undefined
        }
        role={canOpenUraSlot ? "button" : undefined}
        tabIndex={canOpenUraSlot ? 0 : undefined}
        title={
          canOpenUraSlot
            ? `Abrir passo #${uraSlotNav.slotId} no editor do chatbot`
            : undefined
        }
        onKeyDown={
          canOpenUraSlot
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openUraSlotInEditor();
                }
              }
            : undefined
        }
      >
        {displayName && (
          <MessageSenderHeader
            displayName={displayName}
            channelLabel={channelLabel}
            isAiAgent={isAiAgentMessage}
            isChatbot={isChatbotMessage}
            isGroupParticipant={isGroupParticipant && isClient}
            groupName={groupName}
            align={isUser ? "right" : "left"}
            isOwnMessage={isLoggedInUser}
            isTeamPeer={isOtherUser && !isAutomationEmail}
            spokenOnCall={spokenOnCall}
            spokenLabel={spokenLabel}
            color={
              isLoggedInUser
                ? (theme.palette.messages?.userContrast ?? "#ffffff")
                : agentSideVisual
                ? (theme.palette.messages?.othersContrast ?? theme.palette.text.primary)
                : isClient
                ? (theme.palette.messages?.clientContrast ?? theme.palette.text.primary)
                : theme.palette.text.secondary
            }
          />
        )}
        {!displayName && spokenOnCall && (
          <MessageSenderHeader
            spokenOnCall
            spokenLabel={spokenLabel}
            align={isUser ? "right" : "left"}
            color={
              isClient
                ? (theme.palette.messages?.clientContrast ?? theme.palette.text.primary)
                : theme.palette.text.secondary
            }
          />
        )}
        {waForwardLabel && message.type !== "log" && message.type !== "note" && message.type !== "Xpro" && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontWeight: 600,
              opacity: 0.92,
              mb: 0.75,
              mt: displayName ? -0.25 : 0,
              textAlign:
                message.type === "log" || message.type === "note" || message.type === "Xpro"
                  ? "center"
                  : isUser
                  ? "right"
                  : "left",
              color: "inherit",
            }}
          >
            ↪ {waForwardLabel}
          </Typography>
        )}
        {isOutboundFromWhatsappApp && (
          <Box
            sx={{
              display: "flex",
              justifyContent:
                message.type === "log" || message.type === "note" || message.type === "Xpro"
                  ? "center"
                  : isUser
                  ? "flex-end"
                  : "flex-start",
              mb: "6px",
              mt: "-2px",
            }}
          >
            <Tooltip title={`Mensagem enviada fora da plataforma (${getExternalOutboundSenderLabel(message).toLowerCase()}).`}>
              <Chip
                size="small"
                label={getExternalOutboundChipLabel(message)}
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  opacity: 0.95,
                  color:
                    isLoggedInUser
                      ? (theme.palette.messages?.userContrast ?? "#ffffff")
                      : agentSideVisual
                      ? (theme.palette.messages?.othersContrast ?? theme.palette.text.primary)
                      : theme.palette.text.secondary,
                  borderColor:
                    isLoggedInUser
                      ? "rgba(255,255,255,0.55)"
                      : agentSideVisual
                      ? "rgba(0,0,0,0.18)"
                      : "divider",
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            </Tooltip>
          </Box>
        )}
        {isImportedFromPersonalWhatsapp &&
          message.type !== "log" &&
          message.type !== "note" &&
          message.type !== "Xpro" && (
            <Box
              sx={{
                display: "flex",
                justifyContent: isUser ? "flex-end" : "flex-start",
                mb: isOutboundFromWhatsappApp ? "4px" : "6px",
                mt: "-2px",
              }}
            >
              <Tooltip title="Mensagem importada do WhatsApp pessoal conectado ao perfil.">
                <Chip
                  size="small"
                  icon={<PermPhoneMsgOutlinedIcon sx={{ fontSize: "1rem !important" }} />}
                  label="WhatsApp pessoal"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    opacity: 0.95,
                    color:
                      isLoggedInUser
                        ? (theme.palette.messages?.userContrast ?? "#ffffff")
                        : agentSideVisual
                        ? (theme.palette.messages?.othersContrast ?? theme.palette.text.primary)
                        : theme.palette.text.secondary,
                    borderColor:
                      isLoggedInUser
                        ? "rgba(255,255,255,0.55)"
                        : agentSideVisual
                        ? "rgba(0,0,0,0.18)"
                        : "divider",
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
              </Tooltip>
            </Box>
          )}
        {quotedPreviewBlock}
        {message.type !== "log" &&
          message.type !== "note" &&
          message.type !== "Xpro" &&
          threadHints.chips.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                justifyContent: isUser ? "flex-end" : "flex-start",
                mb: "6px",
                mt: isOutboundFromWhatsappApp ? 0 : "-2px",
              }}
            >
              {threadHints.chips.map((c) => (
                <Tooltip key={c.key} title={c.tooltip}>
                  <Chip
                    size="small"
                    label={c.label}
                    variant="outlined"
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      opacity: 0.95,
                      maxWidth: "100%",
                      color: isLoggedInUser
                        ? (theme.palette.messages?.userContrast ?? "#ffffff")
                        : agentSideVisual
                        ? (theme.palette.messages?.othersContrast ?? theme.palette.text.primary)
                        : theme.palette.text.secondary,
                      borderColor: isLoggedInUser
                        ? "rgba(255,255,255,0.55)"
                        : agentSideVisual
                        ? "rgba(0,0,0,0.18)"
                        : "divider",
                      "& .MuiChip-label": { px: 0.75, overflow: "hidden", textOverflow: "ellipsis" },
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          )}
        {renderContent()}

        {waReactionChips && waReactionChips.length > 0 && message.type !== "log" && message.type !== "note" && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              mt: 0.75,
              mb: 0.25,
              justifyContent: getAlignment() === "flex-end" ? "flex-end" : "flex-start",
            }}
          >
            {waReactionChips.map((c) => (
              <Tooltip key={c.key} title={c.tooltip}>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    fontSize: "1.1rem",
                    lineHeight: 1.2,
                    cursor: "default",
                    userSelect: "none",
                  }}
                >
                  {c.label}
                </Typography>
              </Tooltip>
            ))}
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent:
              message.type === "log" || message.type === "note" || message.type === "Xpro"
                ? "center"
                : "flex-end",
            gap: 0.75,
            marginTop: message.type === "log" ? "2px" : "4px",
            flexWrap: "nowrap",
            minWidth: 0,
          }}
        >
          {hasBubbleActions && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                flexShrink: 0,
                borderRadius: 999,
                bgcolor: isLoggedInUser
                  ? "rgba(255,255,255,0.16)"
                  : "rgba(0,0,0,0.06)",
                px: 0.15,
              }}
            >
              {canQuoteMessage && (
                <Tooltip title="Responder">
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      onQuoteWhatsappMessage(message);
                    }}
                    sx={{ padding: "4px", color: bubbleActionColor }}
                    aria-label="Responder"
                  >
                    <ReplyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              {showDeleteAction && (
                <Tooltip title={Number(message?.originTipo) === 6 ? "Apagar no WhatsApp: em geral até 48 horas após o envio" : "Apagar no WhatsApp (Cloud API): dentro de 48h do envio, com entrega confirmada"}>
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      setDeleteDialogOpen(true);
                    }}
                    sx={{ padding: "4px", color: bubbleActionColor }}
                    aria-label="Apagar mensagem"
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              {hasMoreQrActions && (
                <>
                  <Tooltip title="Mais ações">
                    <IconButton
                      size="small"
                      onClick={(e) => setQrActionsAnchor(e.currentTarget)}
                      sx={{ padding: "4px", color: bubbleActionColor }}
                      aria-label="Mais ações da mensagem"
                    >
                      <MoreHorizIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={qrActionsAnchor}
                    open={Boolean(qrActionsAnchor)}
                    onClose={() => setQrActionsAnchor(null)}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    transformOrigin={{ vertical: "bottom", horizontal: "center" }}
                  >
                    {canReactMessage && (
                      <MenuItem
                        onClick={() => {
                          setQrActionsAnchor(null);
                          const emoji = window.prompt("Emoji da reação", "👍");
                          if (emoji) onReactMessage(message.id, emoji);
                        }}
                      >
                        <ListItemIcon>
                          <ThumbUpOffAltIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Reagir" />
                      </MenuItem>
                    )}
                    {canEditMessage && (
                      <MenuItem
                        onClick={() => {
                          setQrActionsAnchor(null);
                          const next = window.prompt("Novo texto", message.text || "");
                          if (next != null) onEditWhatsappMessage(message.id, next);
                        }}
                      >
                        <ListItemIcon>
                          <EditOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Editar" />
                      </MenuItem>
                    )}
                    {canForwardMessage && (
                      <MenuItem
                        onClick={() => {
                          setQrActionsAnchor(null);
                          const to = window.prompt("Encaminhar para (telefone ou grupo)");
                          if (to) onForwardWhatsappMessage(message.id, to);
                        }}
                      >
                        <ListItemIcon>
                          <ForwardIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Encaminhar" />
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </Box>
          )}
          {hasEmailOriginal && !(isEmailChannel && isClient) && (
            <Tooltip title="Abrir e-mail original recebido">
              <IconButton
                size="small"
                onClick={() => setEmailOriginalOpen(true)}
                sx={{
                  padding: "2px",
                  color:
                    message.type === "note" || message.type === "Xpro"
                      ? theme.palette.text.primary
                      : bubbleActionColor,
                }}
                aria-label="Abrir e-mail original"
              >
                <MailOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Typography
            variant="caption"
            sx={{
              textAlign:
                message.type === "log" || message.type === "note" || message.type === "Xpro"
                  ? "center"
                  : "right",
              color:
                message.type === "log" || message.type === "note" || message.type === "Xpro"
                  ? theme.palette.text.primary
                  : isLoggedInUser
                  ? (theme.palette.messages?.userContrast ?? "#ffffff")
                  : agentSideVisual
                  ? (theme.palette.messages?.othersContrast ?? theme.palette.text.primary)
                  : isClient
                  ? (theme.palette.messages?.clientContrast ?? theme.palette.text.primary)
                  : theme.palette.text.secondary,
              fontSize: message.type === "log" ? "0.75rem" : "0.75rem",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {isInboundEmail ? `Recebido em ${displayDate}` : displayDate}
          </Typography>
        </Box>

        {hasEmailOriginal && (
          <EmailOriginalDialog
            open={emailOriginalOpen}
            onClose={() => setEmailOriginalOpen(false)}
            rawEmail={message.emailOriginal}
          />
        )}

        <Dialog open={deleteDialogOpen} onClose={() => !deleteInProgress && setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Apagar mensagem?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {Number(message?.originTipo) === 6
                ? "A mensagem some para todos no WhatsApp. Aqui no chat ela fica marcada como excluída, como uma anotação apagada."
                : "Só é possível quando a API da Meta aceita: em geral até 48 horas após o envio e após a mensagem ter saído do estado \"enviando\" (sem falha de entrega). Aqui no chat ela fica marcada como excluída."}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteInProgress}>
              Cancelar
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={deleteInProgress}
              onClick={async () => {
                if (!onDeleteMessage || message?.id == null) return;
                setDeleteInProgress(true);
                try {
                  await onDeleteMessage(message.id);
                  setDeleteDialogOpen(false);
                } catch (_) {
                  // erro tratado no container (snackbar)
                } finally {
                  setDeleteInProgress(false);
                }
              }}
            >
              {deleteInProgress ? "Apagando…" : "Apagar"}
            </Button>
          </DialogActions>
        </Dialog>

        {isSending && (
          <Typography
            variant="caption"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              marginTop: "2px",
              justifyContent: "flex-end",
              color: isUser && chatBubblePalette ? captionOnBubbleMutedColor : theme.palette.text.secondary,
            }}
          >
            Enviando…{" "}
            <CircularProgress
              size={12}
              sx={{
                color: isUser && chatBubblePalette ? captionOnBubbleMutedColor : "inherit",
              }}
            />
          </Typography>
        )}

        {showDeliveryStatus && !isSending && deliveryDisplay.label && (
          <Box sx={{ width: "100%", marginTop: "2px" }}>
            {deliveryDisplay.errorReason ? (() => {
              const friendlyMsg = isReengagement
                ? REENGAGEMENT_FRIENDLY_MESSAGE
                : getFriendlyDeliveryError(String(deliveryDisplay.errorReason));
              return (
                <Box sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  borderRadius: "6px",
                  padding: "6px 8px",
                  mt: "4px",
                  backgroundColor: alpha(theme.palette.error.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <ErrorOutlineIcon sx={{ fontSize: 14, color: theme.palette.error.main }} />
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.error.main,
                        fontSize: "0.72rem",
                      }}
                    >
                      {deliveryDisplay.label}
                    </Typography>
                  </Box>
                  {friendlyMsg && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: "0.72rem",
                        lineHeight: 1.4,
                      }}
                    >
                      {friendlyMsg}
                    </Typography>
                  )}
                </Box>
              );
            })() : (
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    justifyContent: "flex-end",
                    color: deliveryDisplay.isError
                      ? theme.palette.error.main
                      : captionOnBubbleMutedColor,
                    fontSize: "0.7rem",
                  }}
                >
                  {renderStatusIcon()}
                  <span>{deliveryDisplay.label}</span>
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {isErrorStatus && !showDeliveryStatus && (
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            borderRadius: "6px",
            padding: "6px 8px",
            mt: "4px",
            backgroundColor: alpha(theme.palette.error.main, 0.08),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <ErrorOutlineIcon sx={{ fontSize: 14, color: theme.palette.error.main }} />
              <Typography
                component="span"
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.error.main,
                  fontSize: "0.72rem",
                }}
              >
                Falha ao enviar
              </Typography>
            </Box>
            {message?.errorMessage ? (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "0.72rem",
                  lineHeight: 1.4,
                }}
              >
                {message.errorMessage}
              </Typography>
            ) : (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "0.72rem",
                  lineHeight: 1.4,
                }}
              >
                Não foi possível enviar esta mensagem. Verifique a conexão do canal e tente reenviar.
              </Typography>
            )}
          </Box>
        )}

        {canRetry && (
          <Button
            variant="text"
            size="small"
            color="error"
            sx={{
              mt: 0.5,
              minWidth: 0,
              textTransform: "none",
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
            startIcon={<ErrorOutlineIcon sx={{ fontSize: "14px !important" }} />}
            onClick={() =>
              onSendMessage(
                {
                  message: message.text,
                  file: message.file,
                  audioURL: message.audio,
                  channel: retryChannel,
                  channelType: retryChannelType,
                },
                message.id
              )
            }
          >
            Tentar reenviar
          </Button>
        )}

        <Box data-no-ura-nav onClick={(e) => e.stopPropagation()}>
          <PartnerApiAuditPanel message={message} />
          <AiAgentAuditPanel message={message} />
        </Box>
      </Box>
      </Box>
    </Box>
  );
};

const areEqual = (prev, next) => {
  const a = prev.message || {};
  const b = next.message || {};
  const dsA = a.deliveryStatus;
  const dsB = b.deliveryStatus;
  return (
    a.id === b.id &&
    a.status === b.status &&
    a.text === b.text &&
    (a.editedAt ?? "") === (b.editedAt ?? "") &&
    (a.deletedAt ?? "") === (b.deletedAt ?? "") &&
    (a.editHistory?.length ?? 0) === (b.editHistory?.length ?? 0) &&
    Boolean(a.canEditNote) === Boolean(b.canEditNote) &&
    Boolean(a.canDeleteNote) === Boolean(b.canDeleteNote) &&
    (a.updatedAt || a.lastUpdate || a.timestamp || a.createdAt) ===
      (b.updatedAt || b.lastUpdate || b.timestamp || b.createdAt) &&
    a.file === b.file &&
    a.audio === b.audio &&
    a.image === b.image &&
    a.video === b.video &&
    (a.json_whatsapp ?? "") === (b.json_whatsapp ?? "") &&
    JSON.stringify(a.apiAudit ?? null) === JSON.stringify(b.apiAudit ?? null) &&
    JSON.stringify(a.aiAudit ?? null) === JSON.stringify(b.aiAudit ?? null) &&
    JSON.stringify(a.uraSlot ?? null) === JSON.stringify(b.uraSlot ?? null) &&
    (a.quotedMessageIdWhatsapp ?? "") === (b.quotedMessageIdWhatsapp ?? "") &&
    (a.isForwardedWhatsapp ?? "") === (b.isForwardedWhatsapp ?? "") &&
    (a.quotedMessageConversationWhatsapp ?? "") === (b.quotedMessageConversationWhatsapp ?? "") &&
    JSON.stringify(a.emailOriginalTags ?? null) === JSON.stringify(b.emailOriginalTags ?? null) &&
    (typeof a.document === "object" && typeof b.document === "object"
      ? JSON.stringify(a.document) === JSON.stringify(b.document)
      : a.document === b.document) &&
    (typeof a.location === "object" && typeof b.location === "object"
      ? JSON.stringify(a.location) === JSON.stringify(b.location)
      : a.location === b.location) &&
    (typeof a.contact === "object" && typeof b.contact === "object"
      ? JSON.stringify(a.contact) === JSON.stringify(b.contact)
      : a.contact === b.contact) &&
    a.channel === b.channel &&
    (a.senderName ?? "") === (b.senderName ?? "") &&
    (dsA?.status ?? -1) === (dsB?.status ?? -1) &&
    (dsA?.label ?? "") === (dsB?.label ?? "") &&
    (dsA?.errorReason ?? "") === (dsB?.errorReason ?? "") &&
    prev.quotedLookup === next.quotedLookup &&
    prev.onNavigateToQuotedMessage === next.onNavigateToQuotedMessage &&
    prev.conversationMessages === next.conversationMessages &&
    prev.messageIndex === next.messageIndex &&
    prev.conversationProjectId === next.conversationProjectId &&
    prev.defaultChannel === next.defaultChannel &&
    prev.conversationChannelId === next.conversationChannelId &&
    (prev.conversationChannelLabel ?? "") === (next.conversationChannelLabel ?? "") &&
    JSON.stringify(a.whatsappQrActions ?? null) === JSON.stringify(b.whatsappQrActions ?? null) &&
    (a.channelName ?? "") === (b.channelName ?? "") &&
    (prev.personalThreadClientLabel ?? "") === (next.personalThreadClientLabel ?? "") &&
    JSON.stringify(prev.internalNoteCluster ?? null) ===
      JSON.stringify(next.internalNoteCluster ?? null)
  );
};

export default React.memo(MessageItem, areEqual);
